import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import { Customer, isNilOrEmpty } from "@tastyworks/tastyworks-api"
import { array, object, string, type InferType } from "yup"

const affiliationOptionValues = [
  "hasListedAffiliation",
  "hasIndustryAffiliation",
  "hasPoliticalAffiliation",
  "hasInstitutionalAssets",
  "isInvestmentAdviser",
] as const

export type AffiliationOptions = Array<(typeof affiliationOptionValues)[number]>

export const affiliationsSelectSchema = object({
  hasNoAffiliations: string().oneOf(["true", undefined]),
  affiliations: array()
    .ensure()
    .of(string().oneOf(affiliationOptionValues).required())
    .test(
      "selectsNoneOrSome",
      "yup.common.mixed.required",
      (value, { parent }) => {
        if (parent?.hasNoAffiliations === "true") return isNilOrEmpty(value)
        return !isNilOrEmpty(value)
      }
    ),
})

export const industryAffiliationSchema = object({
  industryAffiliationFirm: string().required(),
})

export const publicCompanyAffiliationSchema = object({
  listedAffiliationSymbol: string().required(),
})

export const politicalAffiliationSchema = object({
  familyMemberNames: string().required(),
  politicalOrganization: string().required(),
})

export const affiliationInformationSchema = object({
  affiliationsSelect: affiliationsSelectSchema,
  publicCompanyAffiliation: publicCompanyAffiliationSchema,
  industryAffiliation: industryAffiliationSchema,
  politicalAffiliation: politicalAffiliationSchema,
})

type AffiliationData = InferType<typeof affiliationInformationSchema>

export const affiliationsSelectAdapter: FormDataAdapter<
  Customer,
  AffiliationData["affiliationsSelect"]
> = {
  toForm: (_, data) => {
    return data
  },
  toModel: (data, customer) => {
    const affiliations = new Map<string, boolean>()

    data.affiliations?.forEach((affiliation) => {
      affiliations.set(affiliation, true)
    })

    customer.hasListedAffiliation = affiliations.has("hasListedAffiliation")
    customer.hasIndustryAffiliation = affiliations.has("hasIndustryAffiliation")
    customer.hasPoliticalAffiliation = affiliations.has(
      "hasPoliticalAffiliation"
    )
    customer.isInvestmentAdviser = affiliations.has("isInvestmentAdviser")
    customer.hasInstitutionalAssets = affiliations.has("hasInstitutionalAssets")

    return customer
  },
  toPatch: (data) => {
    const affiliations = new Map<string, boolean>()
    data.affiliations?.forEach((affiliation) => {
      affiliations.set(affiliation, true)
    })

    return new PatchBuilder<Customer>()
      .boolean("hasListedAffiliation", affiliations.has("hasListedAffiliation"))
      .boolean(
        "hasIndustryAffiliation",
        affiliations.has("hasIndustryAffiliation")
      )
      .boolean(
        "hasPoliticalAffiliation",
        affiliations.has("hasPoliticalAffiliation")
      )
      .boolean("isInvestmentAdviser", affiliations.has("isInvestmentAdviser"))
      .boolean(
        "hasInstitutionalAssets",
        affiliations.has("hasInstitutionalAssets")
      )
      .build()
  },
}

export const industryAffiliationAdapter: FormDataAdapter<
  Customer,
  AffiliationData["industryAffiliation"]
> = {
  toForm: (_, data) => {
    return data
  },
  toModel: (data, customer) => {
    customer.industryAffiliationFirm = data.industryAffiliationFirm
    return customer
  },
  toPatch: (data) =>
    new PatchBuilder<Customer>()
      .string("industryAffiliationFirm", data.industryAffiliationFirm)
      .build(),
}

export const publicCompanyAffiliationAdapter: FormDataAdapter<
  Customer,
  AffiliationData["publicCompanyAffiliation"]
> = {
  toForm: (_, data) => {
    return data
  },
  toModel: (data, customer) => {
    customer.listedAffiliationSymbol = data.listedAffiliationSymbol
    return customer
  },
  toPatch: (data) =>
    new PatchBuilder<Customer>()
      .string("listedAffiliationSymbol", data.listedAffiliationSymbol)
      .build(),
}

export const politicalAffiliationAdapter: FormDataAdapter<
  Customer,
  AffiliationData["politicalAffiliation"]
> = {
  toForm: (_, data) => {
    return data
  },
  toModel: (data, customer) => {
    customer.familyMemberNames = data.familyMemberNames
    customer.politicalOrganization = data.politicalOrganization

    return customer
  },
  toPatch: (data) =>
    new PatchBuilder<Customer>()
      .string("familyMemberNames", data.familyMemberNames)
      .string("politicalOrganization", data.politicalOrganization)
      .build(),
}

export const affiliationInformationAdapter: FormDataAdapter<
  Customer,
  InferType<typeof affiliationInformationSchema>
> = {
  toForm: (customer, data) => {
    data.affiliationsSelect = {
      affiliations: [],
    }
    if (customer.hasListedAffiliation) {
      data.affiliationsSelect?.affiliations?.push("hasListedAffiliation")
      data.publicCompanyAffiliation = {
        listedAffiliationSymbol: customer.listedAffiliationSymbol,
      }
    }
    if (customer.hasIndustryAffiliation) {
      data.affiliationsSelect?.affiliations?.push("hasIndustryAffiliation")
      data.industryAffiliation = {
        industryAffiliationFirm: customer.industryAffiliationFirm,
      }
    }
    if (customer.hasPoliticalAffiliation) {
      data.affiliationsSelect?.affiliations?.push("hasPoliticalAffiliation")
      data.politicalAffiliation = {
        politicalOrganization: customer.politicalOrganization,
        familyMemberNames: customer.familyMemberNames,
      }
    }
    if (customer.isInvestmentAdviser) {
      data.affiliationsSelect?.affiliations?.push("isInvestmentAdviser")
    }
    if (customer.hasInstitutionalAssets) {
      data.affiliationsSelect?.affiliations?.push("hasInstitutionalAssets")
    }

    if (isNilOrEmpty(data.affiliationsSelect.affiliations)) {
      data.affiliationsSelect.hasNoAffiliations = "true"
    }

    return data
  },
  toModel: (data, customer) => {
    affiliationsSelectAdapter.toModel(data.affiliationsSelect, customer)
    publicCompanyAffiliationAdapter.toModel(
      data.publicCompanyAffiliation,
      customer
    )
    politicalAffiliationAdapter.toModel(data.politicalAffiliation, customer)
    industryAffiliationAdapter.toModel(data.industryAffiliation, customer)

    return customer
  },
}
